import React, { useCallback, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useSelector } from 'react-redux'
import { useUserRegion } from '@frontend/components/agromarket/components/RegionSelector'
import { FundingProduct } from '@frontend/components/agromarket/core/access-control'
import { EColor } from '@frontend/pole-ui/lib/common/constants'
import { EChipModifier } from '@frontend/pole-ui/lib/components/Chip'
import { CommonLink } from '@frontend/pole-ui/lib/components/CommonLink'
import { EIconSize } from '@frontend/pole-ui/lib/components/Icon'
import { OkFillIcon } from '@frontend/pole-ui/lib/components/Icon/svg/OkFillIcon'
import { Modal, useModal } from '@frontend/ui-kit/agromarket/components/Modal'
import type { FundingRequestData } from '@/api/kubik/monolith'
import Badge from '@/components/composed/financing/Badge/Badge'
import { useApplyFinancingOffer } from '@/components/composed/financing/hooks'
import { GenericErrorModal } from '@/components/composed/modals/GenericErrorModal'
import { GenericSuccessModal } from '@/components/composed/modals/GenericSuccessModal'
import { Image, TextWithIcon } from '@/components/ui'
import { useAuthenticateUser } from '@/components/ui/AuthButton/hooks'
import { Checkbox } from '@/components/ui/ReactHookFormFields'
import { useData } from '@/logic/data'
import { getLegalDocumentUrl } from '@/logic/legalDocuments/helpers'
import { pushGtmEventOnSubmitOrder } from '@/logic/metrika/financing/gtmHelpers'
import { EGtmBlock } from '@/logic/metrika/financing/types'
import { getUserGtmInfo } from '@/logic/metrika/helpers'
import { getProfile } from '@/logic/profile/reducer'
import type { RootState } from '@/redux/interfaces'
import { EFinancingOfferType } from '@/types'
import type { TLegalDocuments } from '@/types/legalDocuments'
import { ELegalDocumentType } from '@/types/legalDocuments'
import { formatLocalizedString } from '@/utils/formatUtils'
import { getApplyingFinancingOfferFormInitialValues } from '@/utils/getApplyingFinancingOfferFormInitialValues'
import { isApplyingOfferRequestBody } from '@/utils/hooks/applyingFinancingOffer/typeguard'
import { useWebpExtension } from '@/utils/hooks/image'
import { useActionOnLoggedIn } from '@/utils/hooks/useActionOnLoggedIn'
import { useDeviceType } from '@/utils/hooks/useDeviceType'
import { FIELD_EVALUATION_REPORT_DIALOG_PREVIEW_NAMES } from './constants'
import { messages } from './messages'
import type { TFieldEvaluationReportDialogProps } from './types'
import './styles.scss'

const mapState = (state: RootState) => ({
  profile: getProfile()(state),
  userGtmInfo: getUserGtmInfo()(state),
})

export const FieldEvaluationReportDialog = Modal.create<TFieldEvaluationReportDialogProps>(
  ({ comment }: TFieldEvaluationReportDialogProps) => {
    const { remove: closeModal } = useModal()
    const { profile, userGtmInfo } = useSelector(mapState)
    const { isAuthenticated, openAuthModal } = useAuthenticateUser()
    const imageExtension = useWebpExtension()

    const { userRegion } = useUserRegion()

    const previews = useMemo(() => FIELD_EVALUATION_REPORT_DIALOG_PREVIEW_NAMES.map(name => `${name}.${imageExtension}`), [imageExtension])
    const [shouldRequestAfterAuth, setShouldRequestAfterAuth] = useState(false)
    const { applyFinancingOffer, isLoading } = useApplyFinancingOffer()
    const { data: legalDocuments = [] } = useData<TLegalDocuments>('legalDocuments')
    const { isMobile } = useDeviceType()
    const { control, watch, trigger } = useForm<{ agreement: boolean }>({ defaultValues: { agreement: false } })
    const agreementField = watch('agreement')

    const getRequestBody = useCallback(() => {
      const initialValues = getApplyingFinancingOfferFormInitialValues({
        offerType: EFinancingOfferType.REPORT_CTRL2GO,
        productId: FundingProduct.REPORT_CTRL2GO,
        profile,
      })

      return {
        ...initialValues,
        comment,
      }
    }, [comment, profile])

    const createFieldEvaluationReport = useCallback(async () => {
      try {
        const body = getRequestBody()

        if (!isApplyingOfferRequestBody(body)) {
          throw new Error('Invalid request body')
        }

        const response = await applyFinancingOffer({
          body,
          withSuccessModal: false,
        })

        pushGtmEventOnSubmitOrder(
          userGtmInfo,
          userRegion.name,
          response as Pick<FundingRequestData, 'id' | 'multiId'>[],
          [FundingProduct.REPORT_CTRL2GO],
          EGtmBlock.REPORT_CTRL2GO,
        )

        Modal.show(GenericSuccessModal, {
          title: messages.successDialog.title,
          subtitle: messages.successDialog.subtitle,
          button: messages.successDialog.submit,
        })
      } catch (error) {
        Modal.show(GenericErrorModal, {
          title: messages.errorDialog.title,
          subtitle: messages.errorDialog.subtitle,
          button: messages.errorDialog.submit,
        })
      } finally {
        closeModal()
      }
    }, [userRegion.name, getRequestBody, applyFinancingOffer, userGtmInfo, closeModal])

    const handleRequestReport = useCallback(() => {
      trigger('agreement')

      if (!agreementField) {
        return
      }

      if (isAuthenticated) {
        createFieldEvaluationReport()

        return
      }

      setShouldRequestAfterAuth(true)

      openAuthModal({
        onCloseModal: () => {
          setShouldRequestAfterAuth(false)
        },
      })

      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAuthenticated, agreementField, openAuthModal, createFieldEvaluationReport])

    useActionOnLoggedIn(() => {
      if (shouldRequestAfterAuth) {
        setShouldRequestAfterAuth(false)
        createFieldEvaluationReport()
      }
    }, shouldRequestAfterAuth)

    return (
      <Modal
        size="sm"
        title={messages.title}
        primaryButton={{
          text: messages.actions.request,
          buttonProps: {
            variant: 'primary',
            isDisabled: isLoading,
          },
          onClick: handleRequestReport,
          dontCloseModal: !agreementField,
        }}
      >
        <div className="fieldEvaluationReportDialog">
          <div className="fieldEvaluationReportDialog__badges">
            <Badge className="fieldEvaluationReportDialog__badge" text={messages.badges.available} color={EChipModifier.LIGHT_GREEN} />
            <Badge
              className="fieldEvaluationReportDialog__badge"
              text={isMobile ? messages.badges.forAgriLand.short : messages.badges.forAgriLand.full}
              color={EChipModifier.LIGHT_ORANGE}
            />
          </div>

          <div className="fieldEvaluationReportDialog__images">
            {previews.map((src: string) => (
              <div className="fieldEvaluationReportDialog__image" key={src}>
                <Image src={src} isInStatic />
              </div>
            ))}
          </div>
          <div className="fieldEvaluationReportDialog__caption text_super-small text_small-sm">{messages.imagesCaption}</div>

          <h5 className="fieldEvaluationReportDialog__sectionTitle">{messages.benefits.title}</h5>
          <div className="fieldEvaluationReportDialog__list">
            {messages.benefits.items.map((text: string) => (
              <TextWithIcon
                key={text}
                className="fieldEvaluationReportDialog__listItem text_small"
                iconProps={{ component: <OkFillIcon />, size: EIconSize.M, style: { color: EColor.GREEN } }}
                text={text}
              />
            ))}
          </div>

          <div className="fieldEvaluationReportDialog__agreement">
            <Checkbox
              control={control}
              id="agreement"
              name="agreement"
              isMobile={isMobile}
              rules={{ required: messages.agreement.required }}
              errorClassName="fieldEvaluationReportDialog__agreementError"
            >
              <div>
                {formatLocalizedString(messages.agreement.text, {
                  agreement: (
                    <CommonLink
                      url={getLegalDocumentUrl(legalDocuments, ELegalDocumentType.personalDataConsent)}
                      shouldOpenInNewWindow
                      className="underline underline_green color_green"
                    >
                      {messages.agreement.agreement}
                    </CommonLink>
                  ),
                  policy: (
                    <CommonLink
                      url={getLegalDocumentUrl(legalDocuments, ELegalDocumentType.registrationPolicy)}
                      shouldOpenInNewWindow
                      className="underline underline_green color_green"
                    >
                      {messages.agreement.policy}
                    </CommonLink>
                  ),
                })}
              </div>
            </Checkbox>
          </div>
        </div>
      </Modal>
    )
  },
)
